import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { Dialog } from '../../../components/Dialog';

export type DateFilterValue = 'today' | 'tomorrow' | 'day-after-tomorrow' | 'yesterday' | 'day-before-yesterday' | 'this-week' | 'this-month' | 'custom' | 'all';

export interface DateRange {
  from: string;
  to: string;
}

interface DateFilterProps {
  value: DateFilterValue;
  customRange?: DateRange;
  onChange: (value: DateFilterValue, customRange?: DateRange) => void;
}

export const DateFilter: React.FC<DateFilterProps> = ({ value, customRange, onChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange>(
    customRange || { from: '', to: '' }
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    alignRight: boolean;
    alignTop: boolean;
  }>({ alignRight: false, alignTop: false });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filters = [
    { id: 'all', label: 'Todas las fechas' },
    { id: 'day-before-yesterday', label: 'Antes de ayer' },
    { id: 'yesterday', label: 'Ayer' },
    { id: 'today', label: 'Hoy' },
    { id: 'tomorrow', label: 'Mañana' },
    { id: 'day-after-tomorrow', label: 'Pasado mañana' },
    { id: 'this-week', label: 'Esta semana' },
    { id: 'this-month', label: 'Este mes' },
    { id: 'custom', label: 'Rango personalizado' },
  ];

  // Calcular posición del dropdown cuando se abre
  useEffect(() => {
    if (showDropdown && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Determinar si hay espacio a la derecha
      const spaceRight = viewportWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      const alignRight = spaceRight < dropdownRect.width && spaceLeft > spaceRight;

      // Determinar si hay espacio abajo
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const alignTop = spaceBelow < dropdownRect.height && spaceAbove > spaceBelow;

      setDropdownPosition({ alignRight, alignTop });
    }
  }, [showDropdown]);

  const getLabel = () => {
    const filter = filters.find(f => f.id === value);
    if (value === 'custom' && customRange) {
      return `${new Date(customRange.from).toLocaleDateString('es-ES')} - ${new Date(customRange.to).toLocaleDateString('es-ES')}`;
    }
    return filter?.label || 'Filtrar por fecha';
  };

  const handleSelect = (id: DateFilterValue) => {
    if (id === 'custom') {
      setShowCustom(true);
      setShowDropdown(false);
    } else {
      onChange(id);
      setShowDropdown(false);
      setShowCustom(false);
    }
  };

  const handleApplyCustom = () => {
    if (tempRange.from && tempRange.to) {
      onChange('custom', tempRange);
      setShowCustom(false);
    }
  };

  const handleClear = () => {
    onChange('all');
    setShowDropdown(false);
    setShowCustom(false);
    setTempRange({ from: '', to: '' });
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
          value === 'all'
            ? 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
        }`}
      >
        <Calendar size={18} />
        <span className="text-sm font-medium">{getLabel()}</span>
        {value !== 'all' ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="hover:bg-green-200 rounded p-0.5 cursor-pointer inline-flex"
          >
            <X size={16} />
          </span>
        ) : (
          <ChevronDown size={16} />
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />
          <div 
            ref={dropdownRef}
            className={`absolute mt-2 w-56 max-h-[calc(100vh-100px)] overflow-y-auto bg-white rounded-lg shadow-lg border border-slate-200 z-20 ${
              dropdownPosition.alignRight ? 'right-0' : 'left-0'
            } ${
              dropdownPosition.alignTop ? 'bottom-full mb-2 mt-0' : ''
            }`}
          >
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => handleSelect(filter.id as DateFilterValue)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  value === filter.id
                    ? 'bg-green-50 text-green-700 font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Custom Range Dialog */}
      <Dialog 
        isOpen={showCustom} 
        onClose={() => setShowCustom(false)}
        title="Seleccionar rango de fechas"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={tempRange.from}
              onChange={(e) => setTempRange({ ...tempRange, from: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={tempRange.to}
              onChange={(e) => setTempRange({ ...tempRange, to: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => setShowCustom(false)}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleApplyCustom}
            disabled={!tempRange.from || !tempRange.to}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Aplicar
          </button>
        </div>
      </Dialog>
    </div>
  );
};
